import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { PAGAMENTOS_INSCRITOS_LIGA_FILTRADOS } from '../../../services/financeiroService';
import { formatCurrency } from '../../../utils/money';
import { OrigemConfirmacaoBadge } from '../../../components/financeiro';

// Recebe apenas `ligaId` por props — a busca é PRÓPRIA desta aba (mesmo padrão de
// RecebimentosLigaTab.tsx: uma consulta paginada/filtrada fora do trio resumo/serie/quebra do
// hook do hub). Mostra os BOLETOS QUE A LIGA DEVE À INPLAY — dinheiro no sentido liga → InPlay —
// vindos de `pagamentosInscritosLigaFiltrados` (PagamentoInscritosLiga), NUNCA de
// `resumo.totalTaxaPlataforma` (esse campo é a taxa já retida sobre os pagamentos de inscrição
// RECEBIDOS dos atletas — o mesmo dinheiro da aba Recebimentos, tempo passado; ver
// services/financeiroService.ts, comentário acima de PAGAMENTOS_INSCRICAO_FILTRADOS_LIGA).
// AC-A4/PRD AC-9: nenhum total desta aba é somado a nenhum total de Recebimentos, e os dois
// sentidos de dinheiro nunca aparecem no mesmo número. Sem agregação no cliente: os valores são
// os campos individuais devolvidos pelo servidor por boleto, nunca uma soma calculada no cliente
// (não existe, no contrato atual, um total agregado de PagamentoInscritosLiga).

export interface APagarInPlayTabProps {
  ligaId: string;
}

interface PagamentoInscritosLigaItem {
  id: string;
  valor: number;
  quantidadeInscritos: number;
  status: string;
  origemConfirmacao: 'WEBHOOK' | 'MANUAL' | null;
  dataVencimento: string | null;
  dataPagamento: string | null;
  criadoEm: string | null;
  categoriaTorneio: { id: string; nome: string | null } | null;
}

interface PagamentosInscritosLigaFiltradosData {
  pagamentosInscritosLigaFiltrados: {
    items: PagamentoInscritosLigaItem[] | null;
    page: number;
    size: number;
    totalItems: number;
    totalPages: number;
  } | null;
}

interface PagamentosInscritosLigaFiltradosVars {
  ligaId: string;
  categoriaTorneioId?: string;
  status?: string;
  inicio?: string;
  fim?: string;
  page: number;
  size: number;
}

const STATUS_OPTIONS = ['PENDENTE', 'PAGO', 'CANCELADO', 'ESTORNADO'];

const APagarInPlayTab: React.FC<APagarInPlayTabProps> = ({ ligaId }) => {
  const [status, setStatus] = useState('');
  const [inicio, setInicio] = useState('');
  const [fim, setFim] = useState('');

  const { data, loading, error, refetch } = useQuery<
    PagamentosInscritosLigaFiltradosData,
    PagamentosInscritosLigaFiltradosVars
  >(PAGAMENTOS_INSCRITOS_LIGA_FILTRADOS, {
    variables: {
      ligaId,
      status: status || undefined,
      inicio: inicio || undefined,
      fim: fim || undefined,
      page: 0,
      size: 10,
    },
    fetchPolicy: 'no-cache',
  });

  const items = data?.pagamentosInscritosLigaFiltrados?.items ?? [];

  return (
    <div>
      <div className="mb-3">
        <h5 className="fw-700 mb-1">Valores a pagar à InPlay</h5>
        <span className="text-grey-500 font-xsss">
          Boletos que a liga deve à InPlay — sentido de dinheiro liga → InPlay, nunca somado aos
          totais de Recebimentos.
        </span>
      </div>

      <div className="card w-100 border-0 bg-white shadow-xs p-3 mb-3">
        <div className="d-flex flex-wrap gap-3 align-items-end">
          <div>
            <label htmlFor="a-pagar-liga-status" className="d-block font-xsss fw-600 mb-1">
              Status
            </label>
            <select
              id="a-pagar-liga-status"
              className="form-select form-select-sm"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                refetch({ status: e.target.value || undefined });
              }}
            >
              <option value="">Todos</option>
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="a-pagar-liga-inicio" className="d-block font-xsss fw-600 mb-1">
              Data Início
            </label>
            <input
              id="a-pagar-liga-inicio"
              type="date"
              className="form-control form-control-sm"
              value={inicio}
              onChange={(e) => setInicio(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="a-pagar-liga-fim" className="d-block font-xsss fw-600 mb-1">
              Data Fim
            </label>
            <input
              id="a-pagar-liga-fim"
              type="date"
              className="form-control form-control-sm"
              value={fim}
              onChange={(e) => setFim(e.target.value)}
            />
          </div>
        </div>
      </div>

      {error ? (
        <div className="alert alert-danger" role="alert">
          Erro ao carregar valores a pagar à InPlay. Tente novamente.
        </div>
      ) : loading ? (
        <div className="text-center py-4">Carregando valores a pagar à InPlay...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-4">Nenhum boleto a pagar à InPlay encontrado.</div>
      ) : (
        <ul className="list-unstyled mb-0">
          {items.map((item) => (
            <li key={item.id} className="d-flex justify-content-between border-bottom py-2">
              <span>
                {item.categoriaTorneio?.nome ?? 'Sem categoria'} — {item.status}
              </span>
              <OrigemConfirmacaoBadge status={item.origemConfirmacao} />
              <span className="fw-600">{formatCurrency(item.valor)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default APagarInPlayTab;
